/**
 * Boolean search strings, written once and translated into six search interfaces.
 *
 * A search is a list of concepts. Within a concept, synonyms are joined with OR; the
 * concepts are joined with AND; exclusions go last with NOT. Every OR group is wrapped
 * in parentheses, which is what keeps the logic intact in databases that apply AND
 * before OR (ProQuest, Web of Science) and in one that long did the reverse (Scopus,
 * which announced a switch to AND-first precedence for 2025–26).
 *
 * Each interface's syntax, as its own help pages document it:
 *
 * - **PubMed** — uppercase AND/OR/NOT, field tags after each term (`[tiab]`, `[ti]`,
 *   `[Mesh]`), truncation needing four letters before `*`, and proximity as
 *   `"a b"[tiab:~N]`, which works only in title, title/abstract and affiliation and does
 *   not accept wildcards.
 * - **EBSCOhost** — field codes before a group (`TI (…)`, `AB (…)`, `SU (…)`) and the
 *   `Nn` (near, any order) proximity operator.
 * - **ProQuest** — `TI(…)`, `TI,AB(…)` for title or abstract, `SU(…)`, and `NEAR/n`.
 * - **Web of Science** — field tags with `=` (`TS=`, `TI=`, `AB=`), `NEAR/n`, no AND
 *   inside a NEAR expression.
 * - **Scopus** — `TITLE-ABS-KEY(…)`, `TITLE(…)`, `TITLE-ABS(…)`, `INDEXTERMS(…)`,
 *   `W/n`, and `AND NOT` at the end of the string.
 * - **Google Scholar** — OR between synonyms, a space for AND, `-` to exclude,
 *   `intitle:` for titles; no truncation, no proximity, no reliable nesting, and a query
 *   cut off at about 256 characters.
 */

export type SearchField = "any" | "title" | "titleAbstract" | "subject";

export type DatabaseId =
  | "scholar"
  | "pubmed"
  | "ebsco"
  | "proquest"
  | "wos"
  | "scopus";

export const DATABASES: readonly DatabaseId[] = [
  "scholar",
  "pubmed",
  "ebsco",
  "proquest",
  "wos",
  "scopus",
];

export interface Concept {
  id: string;
  /** Synonyms as typed: one per line, or separated by commas, semicolons or OR. */
  terms: string;
  field: SearchField;
}

export interface SearchInput {
  concepts: Concept[];
  /** Terms to exclude with NOT, searched in the default field. */
  exclude: string;
}

export type Term =
  | { kind: "word"; text: string }
  | { kind: "phrase"; text: string }
  | { kind: "near"; left: string; right: string; distance: number };

/** Why a database's string differs from the plain logic — rendered as a note. */
export type SearchNote =
  | "scholarNoTruncation"
  | "scholarNoProximity"
  | "scholarNoField"
  | "scholarLength"
  | "pubmedShortStem"
  | "pubmedProximityField"
  | "pubmedProximityWildcard"
  | "ebscoXb"
  | "proquestTruncation"
  | "wosNoSubject"
  | "scopusAndNot";

export interface DatabaseQuery {
  query: string;
  notes: SearchNote[];
}

/** Google Scholar cuts a query off at about this many characters. */
export const SCHOLAR_MAX_LENGTH = 256;

const QUOTES = /^["“”]+|["“”]+$/g;
const SPACE = /\s/;
const SPACES = /\s+/g;
const NEAR_TERM = /^(\S+)\s+(?:~|near\/?|n|w\/)(\d{1,3})\s+(\S+)$/i;
const OR_SEPARATOR = /\s+OR\s+/;
const WILDCARDS = /[*?#$]/g;
const FIRST_WILDCARD = /[*?#$]/;

/** Split typed terms on new lines, commas and semicolons outside quotes, and on " OR ". */
function splitTerms(raw: string): string[] {
  const parts: string[] = [];
  let current = "";
  let quoted = false;
  for (const char of raw) {
    if (char === '"' || char === "“" || char === "”") {
      quoted = !quoted;
      current += char;
    } else if (!quoted && (char === "\n" || char === "," || char === ";")) {
      parts.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  parts.push(current);
  return parts.flatMap((part) => part.split(OR_SEPARATOR));
}

function toTerm(raw: string): Term | null {
  const clean = raw.trim().replace(SPACES, " ");
  if (clean === "") {
    return null;
  }
  const near = NEAR_TERM.exec(clean);
  if (near) {
    const [, left, distance, right] = near;
    return { kind: "near", left, right, distance: Number(distance) };
  }
  const text = clean.replace(QUOTES, "").trim();
  if (text === "") {
    return null;
  }
  const wasQuoted = text !== clean;
  return wasQuoted || SPACE.test(text)
    ? { kind: "phrase", text }
    : { kind: "word", text };
}

function termKey(term: Term): string {
  return term.kind === "near"
    ? `${term.left} ~${term.distance} ${term.right}`.toLowerCase()
    : term.text.toLowerCase();
}

/** The distinct terms in a box, in the order typed. */
export function parseTerms(raw: string): Term[] {
  const seen = new Set<string>();
  const terms: Term[] = [];
  for (const part of splitTerms(raw)) {
    const term = toTerm(part);
    if (term && !seen.has(termKey(term))) {
      seen.add(termKey(term));
      terms.push(term);
    }
  }
  return terms;
}

function hasWildcard(term: Term): boolean {
  const text = term.kind === "near" ? `${term.left} ${term.right}` : term.text;
  return FIRST_WILDCARD.test(text);
}

/* -------------------------------------------------------------- per database */

interface Ctx {
  notes: Set<SearchNote>;
}

function quoted(text: string): string {
  return `"${text}"`;
}

function plain(term: Exclude<Term, { kind: "near" }>): string {
  return term.kind === "phrase" ? quoted(term.text) : term.text;
}

function group(items: string[]): string {
  return items.length === 1 ? items[0] : `(${items.join(" OR ")})`;
}

/**
 * Render a group's terms, bracketing a proximity term that shares the group with
 * others. Scopus has applied OR before W/n, so `a OR b W/3 c` could read as
 * `(a OR b) W/3 c`; the brackets make every interface read it the same way.
 */
function renderTerms(terms: Term[], render: (term: Term) => string): string[] {
  return terms.map((term) => {
    const text = render(term);
    return term.kind === "near" && terms.length > 1 ? `(${text})` : text;
  });
}

/* PubMed: a tag after every term. */

const PUBMED_TAGS: Record<SearchField, string> = {
  any: "",
  title: "[ti]",
  titleAbstract: "[tiab]",
  subject: "[Mesh]",
};

function pubmedTerm(term: Term, field: SearchField, ctx: Ctx): string {
  if (term.kind === "near") {
    const left = term.left.replace(WILDCARDS, "");
    const right = term.right.replace(WILDCARDS, "");
    if (left !== term.left || right !== term.right) {
      ctx.notes.add("pubmedProximityWildcard");
    }
    if (field !== "title" && field !== "titleAbstract") {
      ctx.notes.add("pubmedProximityField");
    }
    const tag = field === "title" ? "ti" : "tiab";
    return `"${left} ${right}"[${tag}:~${term.distance}]`;
  }
  const at = term.text.search(FIRST_WILDCARD);
  if (at >= 0 && at < 4) {
    ctx.notes.add("pubmedShortStem");
  }
  return `${plain(term)}${PUBMED_TAGS[field]}`;
}

/* EBSCOhost: a code before the group; title-or-abstract spelled out. */

const EBSCO_CODES: Record<Exclude<SearchField, "titleAbstract">, string> = {
  any: "",
  title: "TI",
  subject: "SU",
};

function ebscoTerm(term: Term): string {
  return term.kind === "near"
    ? `${term.left} N${term.distance} ${term.right}`
    : plain(term);
}

function ebscoGroup(terms: Term[], field: SearchField, ctx: Ctx): string {
  const items = renderTerms(terms, ebscoTerm);
  const inner = group(items);
  if (field === "titleAbstract") {
    ctx.notes.add("ebscoXb");
    const parenthesised = items.length === 1 ? `(${inner})` : inner;
    return `(TI ${parenthesised} OR AB ${parenthesised})`;
  }
  const code = EBSCO_CODES[field];
  return code ? `${code} ${inner}` : inner;
}

/* ProQuest: CODE(…) with comma-joined codes for several fields. */

const PROQUEST_CODES: Record<SearchField, string> = {
  any: "",
  title: "TI",
  titleAbstract: "TI,AB",
  subject: "SU",
};

function proquestTerm(term: Term): string {
  return term.kind === "near"
    ? `${term.left} NEAR/${term.distance} ${term.right}`
    : plain(term);
}

function proquestGroup(terms: Term[], field: SearchField): string {
  const items = renderTerms(terms, proquestTerm);
  const code = PROQUEST_CODES[field];
  if (!code) {
    return group(items);
  }
  return `${code}(${items.join(" OR ")})`;
}

/* Web of Science: TAG=(…). Topic stands in for "any" and for subject headings. */

function wosTerm(term: Term): string {
  return term.kind === "near"
    ? `${term.left} NEAR/${term.distance} ${term.right}`
    : plain(term);
}

function wosGroup(terms: Term[], field: SearchField, ctx: Ctx): string {
  const inner = renderTerms(terms, wosTerm).join(" OR ");
  if (field === "title") {
    return `TI=(${inner})`;
  }
  if (field === "titleAbstract") {
    return `(TI=(${inner}) OR AB=(${inner}))`;
  }
  if (field === "subject") {
    ctx.notes.add("wosNoSubject");
  }
  return `TS=(${inner})`;
}

/* Scopus: FIELD(…), W/n proximity. */

const SCOPUS_CODES: Record<SearchField, string> = {
  any: "TITLE-ABS-KEY",
  title: "TITLE",
  titleAbstract: "TITLE-ABS",
  subject: "INDEXTERMS",
};

function scopusTerm(term: Term): string {
  return term.kind === "near"
    ? `${term.left} W/${term.distance} ${term.right}`
    : plain(term);
}

function scopusGroup(terms: Term[], field: SearchField): string {
  return `${SCOPUS_CODES[field]}(${renderTerms(terms, scopusTerm).join(" OR ")})`;
}

/* Google Scholar: no truncation, no proximity, one level of grouping. */

function scholarTerm(term: Term, field: SearchField, ctx: Ctx): string {
  let text: string;
  if (term.kind === "near") {
    ctx.notes.add("scholarNoProximity");
    text = quoted(`${term.left} ${term.right}`.replace(WILDCARDS, ""));
  } else {
    const stripped = term.text.replace(WILDCARDS, "");
    text = term.kind === "phrase" ? quoted(stripped) : stripped;
  }
  if (hasWildcard(term)) {
    ctx.notes.add("scholarNoTruncation");
  }
  if (field === "title") {
    return `intitle:${text}`;
  }
  if (field !== "any") {
    ctx.notes.add("scholarNoField");
  }
  return text;
}

function scholarQuery(
  concepts: { terms: Term[]; field: SearchField }[],
  exclude: Term[],
  ctx: Ctx
): string {
  const parts = concepts.map(({ terms, field }) =>
    group(terms.map((term) => scholarTerm(term, field, ctx)))
  );
  for (const term of exclude) {
    parts.push(`-${scholarTerm(term, "any", ctx)}`);
  }
  const query = parts.join(" ");
  if (query.length > SCHOLAR_MAX_LENGTH) {
    ctx.notes.add("scholarLength");
  }
  return query;
}

/* ------------------------------------------------------------------ assembly */

function conceptGroup(
  db: Exclude<DatabaseId, "scholar">,
  terms: Term[],
  field: SearchField,
  ctx: Ctx
): string {
  if (db === "pubmed") {
    return group(terms.map((term) => pubmedTerm(term, field, ctx)));
  }
  if (db === "ebsco") {
    return ebscoGroup(terms, field, ctx);
  }
  if (db === "proquest") {
    return proquestGroup(terms, field);
  }
  if (db === "wos") {
    return wosGroup(terms, field, ctx);
  }
  return scopusGroup(terms, field);
}

function hasTruncation(terms: Term[]): boolean {
  return terms.some((term) => term.kind !== "near" && term.text.includes("*"));
}

/** The search in one interface's syntax, with the notes that explain it. */
export function buildQuery(input: SearchInput, db: DatabaseId): DatabaseQuery {
  const ctx: Ctx = { notes: new Set() };
  const concepts = input.concepts
    .map((concept) => ({
      terms: parseTerms(concept.terms),
      field: concept.field,
    }))
    .filter((concept) => concept.terms.length > 0);
  const exclude = parseTerms(input.exclude);
  if (concepts.length === 0) {
    return { query: "", notes: [] };
  }
  if (db === "scholar") {
    const query = scholarQuery(concepts, exclude, ctx);
    return { query, notes: [...ctx.notes] };
  }
  let query = concepts
    .map(({ terms, field }) => conceptGroup(db, terms, field, ctx))
    .join(" AND ");
  if (exclude.length > 0) {
    const excluded = conceptGroup(db, exclude, "any", ctx);
    query += db === "scopus" ? ` AND NOT ${excluded}` : ` NOT ${excluded}`;
    if (db === "scopus") {
      ctx.notes.add("scopusAndNot");
    }
  }
  if (db === "proquest" && hasTruncation(concepts.flatMap((c) => c.terms))) {
    ctx.notes.add("proquestTruncation");
  }
  return { query, notes: [...ctx.notes] };
}

/** Every interface at once, in display order. */
export function buildAllQueries(
  input: SearchInput
): Record<DatabaseId, DatabaseQuery> {
  return Object.fromEntries(
    DATABASES.map((db) => [db, buildQuery(input, db)])
  ) as Record<DatabaseId, DatabaseQuery>;
}

function genericTerm(term: Term): string {
  return term.kind === "near"
    ? `${term.left} NEAR/${term.distance} ${term.right}`
    : plain(term);
}

/** The plain logic, with no field codes: `(a OR "b c") AND (d OR e)`. */
export function genericQuery(groups: readonly string[]): string {
  return groups
    .map((raw) => parseTerms(raw))
    .filter((terms) => terms.length > 0)
    .map((terms) => group(renderTerms(terms, genericTerm)))
    .join(" AND ");
}

/**
 * Concepts carried in a link: `?c=nurse*|"nursing staff"&c=burnout`. Lets another tool
 * (the PICO generator) hand its terms to the builder; read in an effect, never during
 * render.
 */
export function conceptsFromQuery(search: string): string[] {
  const params = new URLSearchParams(search);
  return params
    .getAll("c")
    .map((value) => value.split("|").join("\n").trim())
    .filter((value) => value.length > 0)
    .slice(0, 8);
}

export function conceptsToQuery(groups: readonly string[]): string {
  const params = new URLSearchParams();
  for (const raw of groups) {
    const terms = parseTerms(raw);
    if (terms.length > 0) {
      params.append(
        "c",
        terms
          .map((term) =>
            term.kind === "near"
              ? `${term.left} ~${term.distance} ${term.right}`
              : plain(term)
          )
          .join("|")
      );
    }
  }
  return params.toString();
}
